import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { ExplainerFeature } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

/** hex → rgba with alpha, for brand-colour tints. */
export const tint = (hex: string, alpha: number): string => {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/** Inline gradient phrase for slide headlines (brand blue → pink). */
export const Grad = ({ children }: { children: ReactNode }) => (
  <span
    className="bg-clip-text text-transparent"
    style={{ backgroundImage: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
  >
    {children}
  </span>
);

/**
 * Ambient background for a slide — soft brand-gradient blobs plus a per-product
 * accent blob, slowly breathing. Purely decorative.
 */
export const GlowField = ({ accent }: { accent: string }) => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
    <div
      className="absolute -top-32 -left-24 h-[38rem] w-[38rem] rounded-full opacity-50 blur-[110px] animate-breathe motion-reduce:animate-none"
      style={{ background: `radial-gradient(circle at center, ${tint(accent, 0.5)}, transparent 65%)` }}
    />
    <div
      className="absolute top-1/3 -right-24 h-[34rem] w-[34rem] rounded-full opacity-40 blur-[120px] animate-breathe motion-reduce:animate-none"
      style={{
        background: "radial-gradient(circle at center, rgba(255,77,184,0.35), transparent 65%)",
        animationDelay: "1.2s",
      }}
    />
    <div
      className="absolute -bottom-40 left-1/4 h-[30rem] w-[30rem] rounded-full opacity-30 blur-[120px] animate-breathe motion-reduce:animate-none"
      style={{
        background: "radial-gradient(circle at center, rgba(78,156,255,0.4), transparent 65%)",
        animationDelay: "2.1s",
      }}
    />
  </div>
);

/**
 * A macOS-style app window. Wrap mock product UI in this to make a slide's
 * visual read as "the actual product". Floats gently.
 */
export const WindowFrame = ({
  title,
  accent = "#4E9CFF",
  children,
  className,
  float = true,
}: {
  title?: ReactNode;
  accent?: string;
  children: ReactNode;
  className?: string;
  float?: boolean;
}) => (
  <div
    className={cn(
      "w-full overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white shadow-[0_30px_80px_-24px_rgba(40,64,111,0.35)]",
      float && "animate-bounce-subtle motion-reduce:animate-none",
      className,
    )}
  >
    <div className="flex items-center gap-2 border-b border-[#EEF1F6] bg-[#F9FAFC] px-4 py-3">
      <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
      <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
      <span className="h-3 w-3 rounded-full bg-[#28C840]" />
      {title && (
        <span className="ml-3 truncate text-xs font-semibold text-genie-textSecondary">{title}</span>
      )}
      <span
        className="ml-auto h-1.5 w-16 rounded-full"
        style={{ background: `linear-gradient(90deg, ${accent}, #FF4DB8)` }}
      />
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

/** A phone handset frame — for call/dialer mocks. Floats gently. */
export const PhoneFrame = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "mx-auto w-[248px] rounded-[2.4rem] border-[6px] border-[#222B45] bg-[#222B45] shadow-[0_30px_80px_-24px_rgba(40,64,111,0.5)] animate-bounce-subtle motion-reduce:animate-none",
      className,
    )}
  >
    <div className="relative overflow-hidden rounded-[1.9rem] bg-white">
      <div className="absolute left-1/2 top-2 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-black/20" />
      {children}
    </div>
  </div>
);

/**
 * A numbered vertical flow — the workhorse "how it works" visual. Each step
 * animates in, connected by a rail.
 */
export const StepRail = ({
  steps,
  accent = "#4E9CFF",
}: {
  steps: { genieIcon?: GenieIconName; title: string; sub?: string }[];
  accent?: string;
}) => (
  <div className="space-y-1">
    {steps.map((s, i) => (
      <div
        key={s.title}
        className="flex gap-4 animate-in fade-in slide-in-from-bottom-2"
        style={{ animationDelay: `${i * 110}ms`, animationFillMode: "both" } as CSSProperties}
      >
        <div className="flex flex-col items-center">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-lg"
            style={{ background: `linear-gradient(135deg, ${accent}, #FF4DB8)` }}
          >
            {i + 1}
          </span>
          {i < steps.length - 1 && (
            <span className="my-1 w-px flex-1" style={{ background: tint(accent, 0.35) }} />
          )}
        </div>
        <div className="pb-6 pt-1">
          <div className="flex items-center gap-2">
            {s.genieIcon && <GenieIcon name={s.genieIcon} size={20} className="h-5 w-5" />}
            <p className="text-sm font-bold text-[#222B45]">{s.title}</p>
          </div>
          {s.sub && <p className="mt-1 text-xs leading-relaxed text-genie-textSecondary">{s.sub}</p>}
        </div>
      </div>
    ))}
  </div>
);

/** The copy column's feature list — brand-icon chips with label + subtext. */
export const FeatureList = ({
  features,
  accent = "#4E9CFF",
}: {
  features: ExplainerFeature[];
  accent?: string;
}) => (
  <ul className="mt-7 space-y-3">
    {features.map((f) => (
      <li key={f.label} className="flex items-start gap-3">
        <span
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: tint(accent, 0.12) }}
        >
          {f.genieIcon ? (
            <GenieIcon name={f.genieIcon} size={18} className="h-[18px] w-[18px]" />
          ) : (
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accent }} />
          )}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#222B45]">{f.label}</p>
          {f.sub && <p className="text-xs leading-relaxed text-genie-textSecondary">{f.sub}</p>}
        </div>
      </li>
    ))}
  </ul>
);

/** A soft pill/chip — for tool names, statuses, tags inside visuals. */
export const Chip = ({
  children,
  accent = "#4E9CFF",
  tone = "soft",
  className,
  style,
}: {
  children: ReactNode;
  accent?: string;
  tone?: "soft" | "solid" | "outline";
  className?: string;
  style?: CSSProperties;
}) => {
  const toneStyle: CSSProperties =
    tone === "solid"
      ? { background: `linear-gradient(90deg, ${accent}, #FF4DB8)`, color: "#fff" }
      : tone === "outline"
        ? { border: `1px solid ${tint(accent, 0.4)}`, color: "#222B45" }
        : { backgroundColor: tint(accent, 0.12), color: "#222B45" };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold",
        className,
      )}
      style={{ ...toneStyle, ...style }}
    >
      {children}
    </span>
  );
};
